import { API_BASE } from "./api";

/**
 * Signing in (B12, docs/design/signup.md): only same-origin console paths may be returned to,
 * so a crafted `return_to` can never send a person to another site. Control characters are
 * refused because browsers strip tabs and newlines from URLs, which would turn a slash, a tab
 * and another slash into `//`, another origin; a backslash anywhere is refused because browsers
 * read it as a slash. The API applies the same rule (services/googlesignin.ts).
 */
export function safeReturnTo(value: string | null | undefined): string {
  if (!value?.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code < 0x20 || code === 0x7f) return "/";
  }
  const path = value.split(/[?#]/, 1)[0] ?? "";
  if (path === "/signin" || path === "/signup") return "/";
  return value;
}

/** The search `/signin` and `/signup` accept: where to return after signing in. */
export function validateAuthSearch(
  search: Record<string, unknown>,
): Record<string, unknown> & { return_to?: string } {
  return {
    ...search,
    ...(typeof search.return_to === "string" ? { return_to: search.return_to } : {}),
  };
}

/** The Google start link, with where to come back to. The API answers with a redirect. */
export function googleSignInHref(returnTo: string): string {
  const target = new URLSearchParams({ return_to: safeReturnTo(returnTo) });
  return `${API_BASE}/auth/google/start?${target.toString()}`;
}

/**
 * A user agent in plain words for the sessions ledger: the browser, then the system. It is a
 * hint for the person reading it, never a security signal.
 */
export function describeUserAgent(value: string | null): string {
  if (!value) return "Unknown browser";
  const browser = /Edg\//.test(value)
    ? "Edge"
    : /OPR\//.test(value)
      ? "Opera"
      : /Firefox\//.test(value)
        ? "Firefox"
        : /Chrome\//.test(value) && !/Chromium\//.test(value)
          ? "Chrome"
          : /Safari\//.test(value)
            ? "Safari"
            : "Unknown browser";
  const system = /Windows NT/.test(value)
    ? "Windows"
    : /Android/.test(value)
      ? "Android"
      : /iPhone|iPad|iPod/.test(value)
        ? "iOS"
        : /Mac OS X/.test(value)
          ? "macOS"
          : /Linux/.test(value)
            ? "Linux"
            : null;
  return system ? `${browser} on ${system}` : browser;
}
